import { memo, useEffect, useMemo, useState, type ReactElement } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WrapTextRoundedIcon from '@mui/icons-material/WrapTextRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { highlightToHast, normalizeLanguage } from '../../markdown/highlight/highlighter';
import { hastToReact } from '../../markdown/render/hastToReact';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import type { CodeBlockProps } from '../../types';

/** Fences long enough that highlighting them would stall the main thread. */
const HIGHLIGHT_LIMIT = 120_000;

const LANGUAGE_LABELS: Record<string, string> = {
  csharp: 'C#',
  cpp: 'C++',
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  tsx: 'TSX',
  jsx: 'JSX',
  json: 'JSON',
  jsonc: 'JSONC',
  yaml: 'YAML',
  xml: 'XML',
  html: 'HTML',
  css: 'CSS',
  sql: 'SQL',
  bash: 'Bash',
  powershell: 'PowerShell',
  docker: 'Dockerfile',
  graphql: 'GraphQL',
  terraform: 'Terraform',
  bicep: 'Bicep',
  markdown: 'Markdown',
  text: 'Text',
};

const labelFor = (language: string): string =>
  LANGUAGE_LABELS[language] ?? language.charAt(0).toUpperCase() + language.slice(1);

/**
 * A fenced code block: highlighted asynchronously by Shiki, with a plain-text
 * rendering shown first so content is never hidden behind the highlighter and a
 * grammar failure degrades instead of erroring.
 */
export const CodeBlock = memo(function CodeBlock({
  code,
  language,
  showLineNumbers = false,
  wrap = false,
  title,
}: CodeBlockProps) {
  const theme = useTheme();
  const { copy, copied } = useCopyToClipboard();

  const [wrapEnabled, setWrapEnabled] = useState(wrap);
  const [numbersEnabled, setNumbersEnabled] = useState(showLineNumbers);
  const [highlighted, setHighlighted] = useState<ReactElement | null>(null);

  useEffect(() => setWrapEnabled(wrap), [wrap]);
  useEffect(() => setNumbersEnabled(showLineNumbers), [showLineNumbers]);

  const normalized = useMemo(() => normalizeLanguage(language), [language]);
  const lineCount = useMemo(() => (code ? code.split('\n').length : 0), [code]);
  const isEmpty = code.trim() === '';

  useEffect(() => {
    if (isEmpty || code.length > HIGHLIGHT_LIMIT) {
      setHighlighted(null);
      return;
    }

    let cancelled = false;
    highlightToHast(code, normalized)
      .then((tree) => {
        if (!cancelled && tree) setHighlighted(hastToReact(tree));
      })
      .catch(() => {
        if (!cancelled) setHighlighted(null);
      });

    return () => {
      cancelled = true;
    };
  }, [code, normalized, isEmpty]);

  if (isEmpty) return null;

  return (
    <Paper
      variant="outlined"
      className="code-block"
      sx={{
        my: 2.5,
        overflow: 'hidden',
        borderColor: theme.markdown.codeBorder,
        backgroundColor: theme.markdown.codeBackground,
        transition: theme.transitions.create('border-color', { duration: 180 }),
        '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.35) },
        '&:hover .code-block__actions': { opacity: 1 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 0.75,
          minHeight: 42,
          borderBottom: `1px solid ${theme.markdown.codeBorder}`,
          backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.02 : 0.04),
        }}
      >
        <Chip label={labelFor(normalized)} size="small" variant="outlined" sx={{ fontFamily: 'inherit' }} />
        {title && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack
          direction="row"
          spacing={0.25}
          className="code-block__actions"
          sx={{
            opacity: { xs: 1, md: 0.45 },
            transition: theme.transitions.create('opacity', { duration: 150 }),
          }}
        >
          {lineCount > 3 && (
            <Tooltip title={numbersEnabled ? 'Hide line numbers' : 'Show line numbers'}>
              <IconButton
                size="small"
                aria-label={numbersEnabled ? 'Hide line numbers' : 'Show line numbers'}
                aria-pressed={numbersEnabled}
                onClick={() => setNumbersEnabled((value) => !value)}
                color={numbersEnabled ? 'primary' : 'default'}
              >
                <FormatListNumberedRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={wrapEnabled ? 'Disable soft wrap' : 'Enable soft wrap'}>
            <IconButton
              size="small"
              aria-label={wrapEnabled ? 'Disable soft wrap' : 'Enable soft wrap'}
              aria-pressed={wrapEnabled}
              onClick={() => setWrapEnabled((value) => !value)}
              color={wrapEnabled ? 'primary' : 'default'}
            >
              <WrapTextRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Copied' : 'Copy code'}>
            <IconButton size="small" aria-label="Copy code" onClick={() => void copy(code)}>
              <Fade in={copied}>
                <CheckRoundedIcon fontSize="small" color="success" sx={{ position: 'absolute' }} />
              </Fade>
              <Fade in={!copied}>
                <ContentCopyRoundedIcon fontSize="small" />
              </Fade>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        className={[
          'code-block__body',
          numbersEnabled ? 'code-block__body--numbered' : '',
          wrapEnabled ? 'code-block__body--wrap' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        sx={{
          overflowX: wrapEnabled ? 'hidden' : 'auto',
          fontSize: '0.86rem',
          lineHeight: 1.65,
          '& pre': { margin: 0, padding: theme.spacing(1.75, 2) },
        }}
      >
        {highlighted ?? (
          <pre className="shiki shiki--plain">
            <code>
              {code.split('\n').map((line, index) => (
                <span className="line" key={index}>
                  {line}
                  {'\n'}
                </span>
              ))}
            </code>
          </pre>
        )}
      </Box>
    </Paper>
  );
});

export default CodeBlock;
